import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<'latest' | 'hottest'>('latest');

  const category = categories.find((c) => c.slug === slug);
  if (!category) return <NotFound />;

  const categoryArticles = articles
    .filter((a) => a.categorySlug === slug)
    .sort((a, b) => {
      if (sortBy === 'hottest') return (b.views || 0) - (a.views || 0);
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), to: '/' },
    { label: category.name },
  ];

  const seoTitle = `在线吃瓜${category.name} | 91在线吃瓜网黑料 | 免费在线吃瓜视频`;
  const seoDesc = `在线吃瓜${category.name}频道，${category.description}。91在线吃瓜网提供最新最全的${category.name}爆料。在线吃瓜网站, 91在线吃瓜网, 免费在线吃瓜`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`在线吃瓜, 91在线吃瓜网, ${category.name}, 免费在线吃瓜, 在线吃瓜爆料`}
        canonicalUrl={`http://hnqxp01.cn/category/${slug}`}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} - 91在线吃瓜网`,
          description: category.description,
          url: `http://hnqxp01.cn/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '91在线吃瓜网',
            url: 'http://hnqxp01.cn/',
          },
        }}
      />

      <section className="relative w-full min-h-[450px] md:min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-100/90 via-primary-100/30 to-accent-100/40"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-950"></div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-24">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
            <div className="flex-1 w-full">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
                <span className="text-foreground-50 text-sm font-medium whitespace-nowrap">
                  {category.name}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-foreground-50 leading-tight">
                {category.name}
                <br />
                全网追踪
              </h1>
            </div>

            <div className="flex-1 w-full flex flex-col justify-end">
              <div className="mb-6">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <div className="flex items-center gap-6">
                <Link
                  to="/"
                  className="px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-bold rounded-xl hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  线路一入口
                </Link>
                <Link
                  to="/"
                  className="text-foreground-300 text-sm font-medium border-b border-foreground-500 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12 w-full">
            <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
              <div className="aspect-[21/9] w-full">
                <img
                  src={category.imageUrl}
                  alt={`${category.name} - 91在线吃瓜网`}
                  title={`${category.name} - 在线吃瓜爆料`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-950">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-50">
              {category.name} · 全网追踪
            </h2>
            <div className="flex items-center bg-background-800/50 rounded-full p-1">
              <button
                className={`px-5 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'latest'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
                onClick={() => setSortBy('latest')}
              >
                最新
              </button>
              <button
                className={`px-5 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'hottest'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
                onClick={() => setSortBy('hottest')}
              >
                最热
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categoryArticles.map((article) => (
              <ArticleCard
                key={article.id}
                id={article.id}
                title={article.title}
                category={article.category}
                categorySlug={article.categorySlug}
                imageUrl={article.imageUrl}
                imageWidth={article.imageWidth}
                imageHeight={article.imageHeight}
                publishedAt={article.publishedAt}
                isHot={article.isHot}
                isTop={article.isTop}
                views={article.views}
              />
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-lg">该分类暂无内容</p>
            </div>
          )}
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
            <div className="aspect-[21/9] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20notification%20bell%20concept%20with%20soft%20pink%20and%20purple%20glow%2C%20abstract%20digital%20communication%20scene%2C%20warm%20bokeh%20lights%2C%20mysterious%20atmosphere%2C%20editorial%20photography%20style%2C%20artistic%20composition%2C%20dramatic%20shadows&width=2100&height=900&seq=cta-category-sub&orientation=landscape"
                alt="订阅分类更新"
                title="订阅分类更新 - 91在线吃瓜网"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-950/20"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <p className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 mb-3 leading-tight">
                订阅
                <span className="italic font-light text-foreground-400 mx-2">本分类更新</span>
              </p>
              <p className="text-foreground-300 text-sm md:text-base mb-8">
                第一时间获取{category.name}最新爆料
              </p>
              <button className="px-10 py-4 bg-foreground-950 text-background-50 text-sm font-bold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap">
                打开通知
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}